import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useAuthState } from '@/components/context/AuthContext';
import { getNewRelic } from '@/lib/newrelic';
import { setErrorContext } from '@/lib/newrelic/context/errorContext';
import {
  getPageTemplateFromPathname,
  getRedactedQueryParams,
  setCurrentPageTemplate,
} from '@/lib/newrelic/context/pageAttributes';

type NewRelicErrorContextProviderProps = {
  pageProps: Record<string, unknown>;
  children: React.ReactNode;
};

export default function NewRelicErrorContextProvider({
  pageProps,
  children,
}: NewRelicErrorContextProviderProps) {
  const router = useRouter();
  const { isLoggedIn, isGuestLoggedIn, user } = useAuthState();

  const pageTemplate =
    (pageProps?.pageTemplate as string | undefined) ?? getPageTemplateFromPathname(router.pathname);

  if (typeof window !== 'undefined') {
    setCurrentPageTemplate(pageTemplate);
  }

  useEffect(() => {
    const urlQueryParams =
      typeof router.query === 'object' && Object.keys(router.query).length > 0
        ? getRedactedQueryParams(router.query as Record<string, unknown>)
        : undefined;

    const userType: 'guest' | 'logged_in' = isLoggedIn && !isGuestLoggedIn ? 'logged_in' : 'guest';
    const userId = user?.id != null ? user.id : undefined;

    const buildId =
      typeof window !== 'undefined' && window.__NEXT_DATA__?.buildId
        ? window.__NEXT_DATA__.buildId
        : undefined;

    setErrorContext({
      urlQueryParams,
      userType,
      userId,
      pageTemplate,
      routePath: router.pathname,
      buildId,
      vercelEnv: process.env.NEXT_PUBLIC_VERCEL_ENV,
    });

    const newRelic = getNewRelic();
    if (newRelic) {
      const attrs: Record<string, string | undefined> = {
        userType,
        pageTemplate,
        routePath: router.pathname,
        buildId,
        vercelEnv: process.env.NEXT_PUBLIC_VERCEL_ENV,
      };

      Object.entries(attrs).forEach(([key, value]) => {
        newRelic.setCustomAttribute(key, value ?? null, true);
      });
    }
  }, [router.pathname, router.query, isLoggedIn, isGuestLoggedIn, user?.id, pageTemplate]);

  return <>{children}</>;
}
